import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import GreenButton from '../Components/Buttons/Greenbutton';
import Icon from '../Components/Icons/Icon';
import { getProducts } from '../lib/api';
import { formatNpr } from '../lib/currency';
import { getOptimizedImageUrl } from '../lib/imageUrl';

const services = [
  {
    title: 'Eye Examinations',
    caption: 'Comprehensive vision check-ups and diagnostics with modern equipment.',
    icon: 'eye',
    to: '/appointments',
    cta: 'Book a check-up',
  },
  {
    title: 'Eyewear Collection',
    caption: 'Stylish frames and lenses curated for every face and budget.',
    icon: 'glasses',
    to: '/products',
    cta: 'Browse frames',
  },
  {
    title: 'Contact Lenses',
    caption: 'Comfortable and convenient vision correction, fitted for you.',
    icon: 'lens',
    to: '/products',
    cta: 'Explore lenses',
  },
];

function HomePage() {
  const navigate = useNavigate();
  const [previewProducts, setPreviewProducts] = useState([]);

  useEffect(() => {
    let isMounted = true;

    async function loadPreviewProducts() {
      try {
        const products = await getProducts();
        if (isMounted) {
          setPreviewProducts(products.slice(0, 3));
        }
      } catch (_error) {
        if (isMounted) {
          setPreviewProducts([]);
        }
      }
    }

    loadPreviewProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="home-layout">
      <section className="page-card home-hero">
        <div className="home-hero__content">
          <span className="home-hero__eyebrow-badge">
            <Icon name="eye" size={15} />
            Complete Eye Care, Under One Roof
          </span>
          <h1 className="page-card__title home-hero__title">
            See better. <span className="home-hero__title-accent">Look better.</span>
          </h1>
          <p className="page-card__text home-hero__text">
            Your trusted destination in Jawalakhel for comprehensive eye examinations,
            expert treatments, and a curated selection of quality eyewear.
          </p>
          <div className="home-hero__actions">
            <GreenButton
              text="Book Appointment"
              onClick={() => {
                navigate('/appointments');
              }}
            />
            <button
              type="button"
              className="home-hero__secondary"
              onClick={() => {
                navigate('/products');
              }}
            >
              Browse Products
            </button>
          </div>
          <div className="home-hero__trust">
            <span>
              <em className="home-hero__trust-icon"><Icon name="user-check" size={15} /></em>
              Certified Optometrists
            </span>
            <span>
              <em className="home-hero__trust-icon"><Icon name="microscope" size={15} /></em>
              Modern Diagnostics
            </span>
            <span>
              <em className="home-hero__trust-icon"><Icon name="glasses" size={15} /></em>
              Curated Eyewear
            </span>
          </div>
        </div>
        <div className="home-hero__visual">
          <img
            className="home-hero__photo"
            src="/2024-10-12.webp"
            alt="Inside the Lalitpur Eye Care store"
            loading="eager"
          />
          <div className="home-hero__photo-chip home-hero__photo-chip--top">
            <span className="home-hero__photo-chip-icon"><Icon name="map-pin" size={18} /></span>
            <span>
              <strong>Jawalakhel, Lalitpur</strong>
              Sun – Fri · 9:15 AM – 7:40 PM
            </span>
          </div>
          <div className="home-hero__photo-chip home-hero__photo-chip--bottom">
            <span className="home-hero__photo-chip-icon"><Icon name="scan-face" size={18} /></span>
            <span>
              <strong>AI Face Matching</strong>
              Find frames that fit your face
            </span>
          </div>
        </div>
      </section>

      <section className="page-card home-categories">
        <div className="home-section-heading">
          <p className="home-section-heading__eyebrow">What we do</p>
          <h2 className="home-info__title">Our Services</h2>
          <p className="home-section-heading__text">
            Everything your eyes need — exams, eyewear, and expert advice.
          </p>
        </div>
        <div className="home-categories__grid">
          {services.map((service) => (
            <article key={service.title} className="home-categories__item">
              <div className="home-categories__icon"><Icon name={service.icon} size={28} /></div>
              <h3>{service.title}</h3>
              <p>{service.caption}</p>
              <Link className="home-categories__link" to={service.to}>
                {service.cta}
                <span aria-hidden="true"> →</span>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="home-face-banner">
        <div className="home-face-banner__copy">
          <p className="home-face-banner__eyebrow">Try something new</p>
          <h2>Not sure which frames suit your face?</h2>
          <p className="home-face-banner__text">
            Use your camera and let our AI face-shape matching suggest frame styles
            made for you — right from your browser.
          </p>
        </div>
        <Link className="home-face-banner__button" to="/getfacematching">
          Try Face Matching
        </Link>
      </section>

      {previewProducts.length > 0 ? (
        <section className="page-card home-products">
          <div className="home-section-heading">
            <p className="home-section-heading__eyebrow">Fresh picks</p>
            <h2 className="home-info__title">Featured Products</h2>
          </div>
          <div className="home-products__grid">
            {previewProducts.map((product) => (
              <article key={product.id} className="home-products__item">
                <Link className="home-products__media-link" to={`/products/${product.id}`}>
                  {product.image ? (
                    <img
                      className="home-products__image"
                      src={getOptimizedImageUrl(product.image, 600)}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="home-products__image home-products__image--placeholder">
                      {product.category === 'Eye Examinations' ? '👁️' :
                       product.category === 'Eyewear' ? '👓' :
                       product.category === 'Contact Lenses' ? '🔍' : '🛍️'}
                    </div>
                  )}
                </Link>
                <div className="home-products__copy">
                  <h3>
                    <Link to={`/products/${product.id}`}>{product.name}</Link>
                  </h3>
                  <strong>{formatNpr(product.price)}</strong>
                </div>
              </article>
            ))}
          </div>
          <div className="home-section-heading home-section-heading--footer">
            <button
              type="button"
              className="home-section-heading__link"
              onClick={() => {
                navigate('/products');
              }}
            >
              View All Products
            </button>
          </div>
        </section>
      ) : null}

      <section className="home-cta">
        <div className="home-cta__copy">
          <h2>Ready for your next eye check-up?</h2>
          <p>Book an appointment in minutes — walk-ins are welcome too.</p>
        </div>
        <div className="home-cta__actions">
          <GreenButton
            text="Book Appointment"
            onClick={() => {
              navigate('/appointments');
            }}
          />
          <Link className="home-cta__secondary" to="/contact">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
